import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { TaggingStore } from 'stores/Tagging/TaggingStore';
import { NumberStepper } from 'components/ui/NumberStepper/NumberStepper';
import { ReactComponent as EnterIcon } from 'components/icons/enter.svg';
import { ReactComponent as TopInningIcon } from 'components/icons/topInning.svg';
import { ReactComponent as BottomInningIcon } from 'components/icons/bottomInning.svg';
import { useInjection } from 'services/ioc/ioc.react';
import { ServiceTypes } from 'constants/ServiceTypes';
import { HotkeyBadge } from '../HotkeyBadge/HotkeyBadge';
import StoreTagButton from './StoreTagButton/StoreTagButton';
import Notes from './Notes/Notes';
import { SessionStore } from '../../../../stores/SessionStore';
import { TeamName } from './TeamName/TeamName';
import {
  ScoreBarDivider,
  ScoreBarOuter,
  ScoreBarWrapper,
  ScoreLabel,
  ScoreLabelBalls,
  StoreTagButtonAndHotkeyWrapper,
} from './ScoreBarStyles';

const ScoreBar: React.FC = observer(() => {
  const taggingStore = useInjection<TaggingStore>(ServiceTypes.TaggingStore);
  const sessionStore = useInjection<SessionStore>(ServiceTypes.SessionStore);
  const homeTeamName = sessionStore.currentSessionMetadata.homeTeam?.name ?? '';
  const awayTeamName = sessionStore.currentSessionMetadata.awayTeam?.name ?? '';
  const { playStore } = taggingStore;
  const { selectedPlay } = playStore;

  const [stepperShowcase, setStepperShowcase] = useState(new Array<boolean>(5));

  const showStepper = (i: number): void => {
    const arr = new Array<boolean>(6);
    if (i > 5) {
      setStepperShowcase(arr);
      return;
    }

    arr[i] = true;
    setStepperShowcase(arr);
    setTimeout(() => showStepper(i + 1), 500);
  };

  useEffect(() => {
    setTimeout(() => showStepper(0), 1000);
  }, []);

  const storeTag = (): void => {
    playStore.storeTag();
  };

  const handleOnChangeCallback = (note: string): void => {
    if (selectedPlay) {
      selectedPlay.note = note;
    }
  };

  const isSuccessButton = (): boolean | undefined => {
    return selectedPlay && !selectedPlay.isChanged && selectedPlay.isTagStored;
  };

  const handleValueChangeIncreaseDecreaseInning = (changedBy: number): void => {
    if (changedBy > 0) {
      playStore.setIncreaseInningRequest(true);
    } else if (!playStore.inningManager.getIsTopOfFirstInning()) {
      playStore.setDecreaseInningRequest(true);
    }
  };

  return (
    <ScoreBarOuter>
      <ScoreBarWrapper>
        <TeamName teamName={awayTeamName} isHomeTeam={false} />
        <NumberStepper
          value={selectedPlay ? selectedPlay.awayTeamScore.total : 0}
          valueChange={(value, changedBy): void => {
            selectedPlay && (selectedPlay.awayTeamScore.adjustedBy += changedBy);
          }}
          valueSpanClassName='score'
          minValue={0}
          isAlwaysVisible={stepperShowcase[0]}
        />
        <span className='score match-score'>-</span>
        <NumberStepper
          value={selectedPlay ? selectedPlay.homeTeamScore.total : 0}
          valueChange={(value, changedBy): void => {
            selectedPlay && (selectedPlay.homeTeamScore.adjustedBy += changedBy);
          }}
          valueSpanClassName='score'
          minValue={0}
          isAlwaysVisible={stepperShowcase[1]}
        />
        <TeamName teamName={homeTeamName} isHomeTeam />
        <ScoreBarDivider />
        <ScoreLabelBalls>B</ScoreLabelBalls>
        <NumberStepper
          value={selectedPlay ? selectedPlay.balls.total : 0}
          valueChange={(value, changedBy): void => {
            selectedPlay && (selectedPlay.balls.adjustedBy += changedBy);
          }}
          valueSpanClassName='score'
          minValue={0}
          isAlwaysVisible={stepperShowcase[2]}
        />
        <ScoreLabel>S</ScoreLabel>
        <NumberStepper
          value={selectedPlay ? selectedPlay.strikes.total : 0}
          valueChange={(value, changedBy): void => {
            selectedPlay && (selectedPlay.strikes.adjustedBy += changedBy);
          }}
          valueSpanClassName='score'
          minValue={0}
          isAlwaysVisible={stepperShowcase[3]}
        />
        <ScoreLabel>O</ScoreLabel>
        <NumberStepper
          value={selectedPlay ? selectedPlay.outs.total : 0}
          valueChange={(value, changedBy): void => {
            selectedPlay && (selectedPlay.outs.adjustedBy += changedBy);
          }}
          valueSpanClassName='score'
          minValue={0}
          isAlwaysVisible={stepperShowcase[4]}
        />
        <ScoreBarDivider />
        <NumberStepper
          value={playStore.inningManager.getInning(0).number}
          valueChange={(value, changedBy): void => handleValueChangeIncreaseDecreaseInning(changedBy)}
          valueSpanClassName='score'
          minValue={0}
          isAlwaysVisible={stepperShowcase[5]}
        />
        {playStore.inningManager.getInning(0).isTop ? (
          <TopInningIcon className='inning-direction' />
        ) : (
          <BottomInningIcon className='inning-direction' />
        )}
        <ScoreBarDivider />
      </ScoreBarWrapper>
      <Notes
        value={selectedPlay ? selectedPlay.note : ''}
        onChangeCallback={handleOnChangeCallback}
        disabled={playStore.isPlaceholderPlaySelected || playStore.playCount === 0}
        onFocusIn={(): void => taggingStore.disableEnableHotkeys(true)}
        onFocusOut={(): void => taggingStore.disableEnableHotkeys(false)}
        suggestions={[
          'Incorrect or missed pitch',
          'Wrong batter or pitcher',
          'Batter or pitcher not in lineup',
          'Low confidence in play outcome',
        ]}
      />
      <StoreTagButtonAndHotkeyWrapper>
        <HotkeyBadge hotkey={<EnterIcon />} />
        <StoreTagButton
          intent={isSuccessButton() ? 'secondary' : 'primary'}
          label={isSuccessButton() ? 'Tag Stored' : 'Save Tag'}
          onClickCallback={storeTag}
          onHotkeyPressCallback={storeTag}
          keyCode={13}
          isDisabled={!selectedPlay || !selectedPlay.canBeSaved}
          data-test='save-tag-button'
        />
      </StoreTagButtonAndHotkeyWrapper>
    </ScoreBarOuter>
  );
});

export default ScoreBar;
